import { useTranslation } from 'react-i18next'
import { List, MessageSquare, Paperclip, Plus, Tag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { computeChargesTotal } from '@/components/shared'
import { InvoiceAttachments } from '@/components/billing/InvoiceAttachments'
import {
  InvoiceLifecycleCard,
  type InvoiceLifecycleStep,
} from '@/components/billing/InvoiceLifecycleCard'
import { defaultFeeImageUrl } from '@/lib/optionalFeeIcons'
import { DEFAULT_CATALOG_IMAGE_URL } from '@/lib/catalogImages'
import { cn } from '@/lib/utils'
import type { InvoiceDetail, LineItem, Surcharge } from '@/types'
import { InvoiceTabSectionCard } from './InvoiceTabSection'
import { formatInvoiceAmount, invoiceTabThClass } from './invoiceTabStyles'

type InvoiceLineTableRow = {
  key: string
  description: string
  imageUrl: string | null
  isDefaultIcon?: boolean
  isOptional: boolean
  isDiscount?: boolean
  detail?: string
  quantity: number
  unitPrice: number
  taxRate: number
  lineTotal: number
}

type ParsedInvoiceNote = {
  meta: string
  body: string
}

function roundMoney(value: number) {
  return Math.round(value * 100) / 100
}

function lineTotalInclVat(amount: number, taxRate: number) {
  return roundMoney((amount || 0) + ((amount || 0) * (taxRate || 0)) / 100)
}

function formatTaxRate(rate: number) {
  return Number.isInteger(rate) ? String(rate) : String(rate).replace('.', ',')
}

function buildVatRows(
  items: LineItem[],
  surcharges: Surcharge[],
  discountAmount: number
) {
  const basesByRate = new Map<number, number>()
  const addBase = (rate: number, amount: number) => {
    basesByRate.set(rate, roundMoney((basesByRate.get(rate) ?? 0) + amount))
  }

  items.forEach(item => {
    addBase(
      item.tax_rate || 0,
      item.line_total ??
        roundMoney((item.quantity || 0) * (item.unit_price || 0))
    )
  })
  surcharges.forEach(surcharge => {
    addBase(surcharge.tax_rate ?? 0, surcharge.amount || 0)
  })

  const baseTotal = Array.from(basesByRate.values()).reduce(
    (sum, amount) => roundMoney(sum + amount),
    0
  )
  const safeDiscount = Math.min(roundMoney(discountAmount), baseTotal)
  const discountedBases = new Map(basesByRate)

  if (safeDiscount > 0 && baseTotal > 0) {
    let remaining = safeDiscount
    const entries = Array.from(discountedBases.entries())
    entries.forEach(([rate, base], index) => {
      const allocated =
        index === entries.length - 1
          ? remaining
          : roundMoney(safeDiscount * (base / baseTotal))
      const safeAllocated = Math.min(allocated, base, remaining)
      discountedBases.set(rate, roundMoney(Math.max(0, base - safeAllocated)))
      remaining = roundMoney(remaining - safeAllocated)
    })
  }

  return Array.from(discountedBases.entries())
    .filter(([, base]) => base > 0)
    .sort(([a], [b]) => a - b)
    .map(([rate, base]) => ({
      rate,
      amount: roundMoney(base * (rate / 100)),
    }))
    .filter(row => row.amount > 0)
}

function parseInvoiceNotes(
  notes: string | null | undefined
): ParsedInvoiceNote[] {
  if (!notes?.trim()) return []

  const entries: ParsedInvoiceNote[] = []
  let current: ParsedInvoiceNote | null = null

  for (const line of notes.split('\n')) {
    const match = line.match(
      /^\[(\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} - .+?)\] (.*)$/
    )
    if (match) {
      if (current) entries.push(current)
      current = { meta: match[1], body: match[2] }
      continue
    }

    if (current) {
      current.body = current.body ? `${current.body}\n${line}` : line
    } else if (line.trim()) {
      current = { meta: '', body: line }
    }
  }

  if (current) entries.push(current)

  return entries
}

export function InvoiceDetailsTab({
  invoice,
  canEditNotes,
  noteDraft,
  notesDirty,
  savingNotes,
  lifecycleSteps,
  lifecycleIsNeutral,
  onNotesChange,
  onSaveNotes,
  onOpenHistory,
}: {
  invoice: InvoiceDetail
  canEditNotes: boolean
  noteDraft: string
  notesDirty: boolean
  savingNotes: boolean
  lifecycleSteps: InvoiceLifecycleStep[]
  lifecycleIsNeutral: boolean
  onNotesChange: (notes: string) => void
  onSaveNotes: () => void
  onOpenHistory: () => void
}) {
  const { t } = useTranslation(['invoices', 'shared'])
  const { t: ts } = useTranslation('shared')
  const lineItems = invoice.items ?? []
  const optionalCharges = invoice.surcharges ?? []
  const parsedNotes = parseInvoiceNotes(invoice.notes)
  const surchargeTotal =
    invoice.surcharge_total ??
    computeChargesTotal(
      optionalCharges.map(s => ({
        type: s.type,
        description: s.description ?? '',
        amount: s.amount,
      }))
    )
  const discountAmount = invoice.discount_amount ?? 0
  const lineRows: InvoiceLineTableRow[] = [
    ...lineItems.map((item, index) => ({
      key: item.id ?? `line-${index}`,
      description: item.description,
      detail: item.service_description?.trim()
        ? item.service_description
        : undefined,
      imageUrl: item.image_url ?? DEFAULT_CATALOG_IMAGE_URL,
      isOptional: false,
      quantity: item.quantity,
      unitPrice: item.unit_price ?? 0,
      taxRate: item.tax_rate,
      lineTotal: lineTotalInclVat(
        item.line_total ??
          roundMoney((item.quantity || 0) * (item.unit_price || 0)),
        item.tax_rate
      ),
    })),
    ...optionalCharges.map((charge, index) => {
      const description = charge.description?.trim()

      return {
        key: charge.id ?? `optional-${index}`,
        description: description || ts(`optionalCharges.types.${charge.type}`),
        imageUrl: charge.image_url || defaultFeeImageUrl(charge.type),
        isDefaultIcon: !charge.image_url,
        isOptional: true,
        quantity: 1,
        unitPrice: charge.amount,
        taxRate: charge.tax_rate ?? 0,
        lineTotal: lineTotalInclVat(charge.amount, charge.tax_rate ?? 0),
      }
    }),
    ...(discountAmount > 0
      ? [
          {
            key: 'trade-discount',
            description: t('invoices:form.tradeDiscountCard.rowTitle'),
            detail: t('invoices:form.tradeDiscountCard.rowDescription'),
            imageUrl: null,
            isOptional: false,
            isDiscount: true,
            quantity: 0,
            unitPrice: 0,
            taxRate: 0,
            lineTotal: discountAmount,
          },
        ]
      : []),
  ]
  const vatRows = buildVatRows(lineItems, optionalCharges, discountAmount)
  const totalExcludingVat = roundMoney(
    invoice.subtotal + surchargeTotal - discountAmount
  )
  const summaryRows = [
    {
      label: t('invoices:detail.subtotal'),
      value: invoice.subtotal,
    },
    ...(surchargeTotal > 0
      ? [
          {
            label: t('invoices:detail.optionalCharges'),
            value: surchargeTotal,
          },
        ]
      : []),
    ...(discountAmount > 0
      ? [
          {
            label: t('invoices:detail.tradeDiscount'),
            value: -discountAmount,
            isDiscount: true,
          },
        ]
      : []),
    {
      label: t('invoices:detail.totalExcludingVat'),
      value: totalExcludingVat,
      isTotalExcludingVat: true,
    },
    ...vatRows.map(row => ({
      label: t('invoices:detail.vatRate', {
        rate: formatTaxRate(row.rate),
      }),
      value: row.amount,
    })),
  ]
  const linesHeadClass = cn(
    invoiceTabThClass,
    'bg-[#fbfbfc] text-[10px] font-medium uppercase leading-[14px] text-[#3d434e]'
  )
  const linesCellClass =
    'h-16 border-b border-border px-3 py-2 text-sm font-medium leading-[1.4] text-[#171a20]'

  return (
    <div className='flex flex-col gap-4'>
      <InvoiceLifecycleCard
        steps={lifecycleSteps}
        neutralMessage={
          lifecycleIsNeutral
            ? t('invoices:detail.lifecycle.cancelledViaCreditNote')
            : null
        }
        onHistoryClick={onOpenHistory}
      />

      <InvoiceTabSectionCard className='gap-0 py-0'>
        <div className='flex h-16 items-center gap-2 px-6 pb-2 pt-6'>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <span className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#fff1ea] p-2 text-primary'>
              <List className='size-4' />
            </span>
            <h2 className='font-heading truncate text-sm font-semibold leading-[1.2] text-[#171a20]'>
              {t('invoices:detail.lineItems')}
            </h2>
          </div>
        </div>

        <div className='flex flex-col gap-4 px-6 pb-6 pt-4'>
          <div className='-mx-6 overflow-x-auto px-6 pb-1'>
            <Table className='min-w-[976px] table-fixed border-b border-border'>
              <TableHeader>
                <TableRow>
                  <TableHead className={cn(linesHeadClass, 'w-16 text-center')}>
                    {t('invoices:detail.columns.image')}
                  </TableHead>
                  <TableHead className={linesHeadClass}>
                    {t('invoices:detail.columns.service')}
                  </TableHead>
                  <TableHead
                    className={cn(linesHeadClass, 'w-40 text-center 2xl:w-60')}
                  >
                    {t('invoices:detail.columns.quantity')}
                  </TableHead>
                  <TableHead
                    className={cn(linesHeadClass, 'w-40 text-center 2xl:w-60')}
                  >
                    {t('invoices:detail.columns.unitPrice')}
                  </TableHead>
                  <TableHead
                    className={cn(linesHeadClass, 'w-40 text-center 2xl:w-60')}
                  >
                    {t('invoices:detail.columns.vatRate')}
                  </TableHead>
                  <TableHead
                    className={cn(linesHeadClass, 'w-40 text-center 2xl:w-60')}
                  >
                    {t('invoices:detail.columns.lineTotal')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className='h-16 text-center text-sm text-muted-foreground'
                    >
                      {t('invoices:detail.noLineItems')}
                    </TableCell>
                  </TableRow>
                ) : (
                  lineRows.map(row => (
                    <TableRow
                      key={row.key}
                      className={cn('h-16', row.isDiscount && 'bg-[#ecfdf5]')}
                    >
                      <TableCell
                        className={cn(
                          linesCellClass,
                          'text-center',
                          row.isDiscount && 'bg-[#ecfdf5]'
                        )}
                      >
                        {row.isDiscount ? (
                          <div className='mx-auto flex size-12 items-center justify-center rounded-md bg-white p-2 text-[#3d434e]'>
                            <Tag className='size-4' />
                          </div>
                        ) : row.imageUrl ? (
                          <div className='mx-auto flex size-12 items-center justify-center overflow-hidden rounded-md bg-[#fff1ea]'>
                            <img
                              src={row.imageUrl}
                              alt=''
                              className={
                                row.isDefaultIcon
                                  ? 'size-4 object-contain'
                                  : 'size-auto object-cover object-center'
                              }
                            />
                          </div>
                        ) : (
                          <div className='mx-auto flex size-12 items-center justify-center overflow-hidden rounded-md bg-[#fff1ea]'>
                            <img
                              src={DEFAULT_CATALOG_IMAGE_URL}
                              alt=''
                              className='size-4 object-contain'
                            />
                          </div>
                        )}
                      </TableCell>
                      <TableCell
                        className={cn(
                          linesCellClass,
                          row.isDiscount && 'bg-[#ecfdf5]'
                        )}
                      >
                        <div className='flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-2'>
                          <span
                            className={cn(
                              'min-w-0 whitespace-pre-wrap break-words',
                              row.isDiscount && 'uppercase'
                            )}
                          >
                            {row.description}
                          </span>
                          {row.isOptional && (
                            <span className='inline-flex shrink-0 items-center gap-1 text-xs font-normal leading-4 text-[#6c7285]'>
                              <span className='size-1 rounded-full bg-[#6c7285]' />
                              {t('invoices:detail.optionalFee')}
                            </span>
                          )}
                          {row.detail ? (
                            <span className='basis-full whitespace-pre-wrap break-words text-xs font-normal leading-4 text-[#3d434e]'>
                              {row.detail}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          linesCellClass,
                          'text-center',
                          row.isDiscount && 'bg-[#ecfdf5]'
                        )}
                      >
                        {row.isDiscount ? '-' : row.quantity}
                      </TableCell>
                      <TableCell
                        className={cn(
                          linesCellClass,
                          'text-center',
                          row.isDiscount && 'bg-[#ecfdf5]'
                        )}
                      >
                        {row.isDiscount
                          ? '-'
                          : formatInvoiceAmount(row.unitPrice)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          linesCellClass,
                          'text-center',
                          row.isDiscount && 'bg-[#ecfdf5]'
                        )}
                      >
                        {row.isDiscount
                          ? '-'
                          : `${formatTaxRate(row.taxRate)}%`}
                      </TableCell>
                      <TableCell
                        className={cn(
                          linesCellClass,
                          'text-center',
                          row.isDiscount && 'bg-[#ecfdf5] text-[#059669]'
                        )}
                      >
                        {row.isDiscount ? '-' : ''}
                        {formatInvoiceAmount(row.lineTotal)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className='flex w-full justify-end bg-white px-6 py-6'>
          <div className='flex w-full max-w-[400px] flex-col gap-3 text-sm leading-[1.4]'>
            {summaryRows.map(row => (
              <div
                key={row.label}
                className={cn(
                  'flex items-center justify-end gap-2 px-3',
                  row.isTotalExcludingVat &&
                    'mt-1 border-t border-[#e7e9ef] pt-3 font-bold text-[#171a20]'
                )}
              >
                <span
                  className={cn(
                    'min-w-0 flex-1',
                    row.isDiscount
                      ? 'text-[#059669]'
                      : row.isTotalExcludingVat
                        ? 'text-[#171a20]'
                        : 'text-[#6c7285]'
                  )}
                >
                  {row.label}
                </span>
                <span
                  className={cn(
                    'w-[120px] shrink-0 text-right font-medium',
                    row.isDiscount ? 'text-[#059669]' : 'text-[#171a20]'
                  )}
                >
                  {formatInvoiceAmount(row.value)}
                </span>
              </div>
            ))}
            <div className='font-heading mt-1 flex items-center justify-end gap-2 rounded-xl bg-[#f4f5f8] p-3 text-xl font-bold leading-[1.2] text-[#171a20]'>
              <span className='min-w-0 flex-1'>
                {t('invoices:detail.totalInclVat')}
              </span>
              <span className='w-[120px] shrink-0 text-right'>
                {formatInvoiceAmount(invoice.total)}
              </span>
            </div>
          </div>
        </div>
      </InvoiceTabSectionCard>

      <div className='grid gap-4 lg:grid-cols-2'>
        <InvoiceTabSectionCard className='min-h-[280px] min-w-0 gap-0 py-0 shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'>
          <div className='flex h-16 items-center gap-2 px-6 pb-2 pt-6'>
            <div className='flex min-w-0 flex-1 items-center gap-3'>
              <span className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#fbf3d9] p-2 text-[#e6a600]'>
                <MessageSquare className='size-4' />
              </span>
              <h2 className='font-heading truncate text-sm font-semibold leading-[1.2] text-[#171a20]'>
                {t('invoices:detail.internalNotes')}
              </h2>
            </div>
          </div>

          <div className='flex min-h-0 flex-1 flex-col'>
            <div className='flex max-h-[336px] min-h-0 flex-col gap-2 overflow-y-auto px-6 pb-4 pt-4'>
              {parsedNotes.length > 0 ? (
                <div className='flex flex-col gap-2'>
                  {parsedNotes.map((note, index) => (
                    <div
                      key={`${note.meta}-${index}`}
                      className='flex flex-col gap-2 rounded-xl bg-[#f4f5f8] px-4 py-3'
                    >
                      {note.meta && (
                        <p className='text-xs font-medium leading-4 text-[#3d434e]'>
                          {note.meta}
                        </p>
                      )}
                      <p className='whitespace-pre-wrap break-words text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {note.body}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='text-sm leading-[1.4] text-[#3d434e]'>
                  {t('invoices:detail.noNotes')}
                </div>
              )}
            </div>

            <div className='mt-auto flex shrink-0 items-start gap-4 px-6 pb-6 pt-4'>
              <Textarea
                value={noteDraft}
                readOnly={!canEditNotes}
                rows={1}
                maxLength={2000}
                className='min-h-10 flex-1 resize-none rounded-xl border-[#e7e9ef] bg-[#f4f5f8] px-4 py-2.5 text-sm leading-[1.4] text-[#171a20] placeholder:text-[#6c7285] focus-visible:ring-[#ff5a1f]'
                placeholder={
                  canEditNotes ? t('invoices:detail.writeNote') : undefined
                }
                onChange={e => onNotesChange(e.target.value)}
              />
              {canEditNotes && (
                <Button
                  type='button'
                  variant='ghost'
                  className='h-10 shrink-0 gap-2 rounded-xl px-3 py-3 text-sm font-medium leading-none text-primary hover:bg-[#fff1ea]'
                  disabled={!notesDirty || savingNotes}
                  onClick={onSaveNotes}
                >
                  <Plus className='size-4' />
                  {t('invoices:detail.addNote')}
                </Button>
              )}
            </div>
          </div>
        </InvoiceTabSectionCard>

        <InvoiceTabSectionCard className='gap-0 py-0'>
          <div className='flex h-16 items-center gap-2 px-6 pb-2 pt-6'>
            <div className='flex min-w-0 flex-1 items-center gap-3'>
              <span className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#fbf3d9] p-2 text-[#e6a600]'>
                <Paperclip className='size-4' />
              </span>
              <h2 className='font-heading truncate text-sm font-semibold leading-[1.2] text-[#171a20]'>
                {t('invoices:detail.attachFiles')}
              </h2>
            </div>
          </div>

          <div className='min-h-0 flex-1 px-6 pb-6 pt-4'>
            <InvoiceAttachments
              invoiceId={invoice.id}
              clientId={invoice.client_id}
              readOnly={!canEditNotes}
              hideHeading
            />
          </div>
        </InvoiceTabSectionCard>
      </div>
    </div>
  )
}
